<script setup lang="ts">
import FormDateInput from '@/Components/Molecules/FormDateInput.vue';
import SelectButton from '@/Components/Molecules/SelectButton.vue';
import ToggleSwitch from '@/Components/Molecules/ToggleSwitch.vue';
import { useTranslations } from '@/composables/useTranslations';
import { usePage } from '@inertiajs/vue3';
import { computed, ref } from 'vue';

const { t } = useTranslations();
const attemptedNext = ref(false);

// Calculate minimum date (today)
const minDate = computed(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
});

interface Props {
    tripType: 'oneway' | 'roundtrip';
    onewayDirection: 'arrival' | 'departure';
    from: string;
    dropTo: string;
    hotel: string;
    adults: number;
    children: number;
    babies: number;
    dateOneWay: string;
    dateArrival: string;
    dateDeparture: string;
    timeOneWay: string;
    timeArrival: string;
    timeDeparture: string;
    pickUpTimeOneWay: string;
    pickUpTimeDeparture: string;
    dateFormatHint: string;
    locationsWithKeys: [string, unknown][];
}

const props = defineProps<Props>();

const emit = defineEmits<{
    (e: 'update:tripType', value: 'oneway' | 'roundtrip'): void;
    (e: 'update:onewayDirection', value: 'arrival' | 'departure'): void;
    (e: 'update:from', value: string): void;
    (e: 'update:dropTo', value: string): void;
    (e: 'update:hotel', value: string): void;
    (e: 'update:adults', value: number): void;
    (e: 'update:children', value: number): void;
    (e: 'update:babies', value: number): void;
    (e: 'update:dateOneWay', value: string): void;
    (e: 'update:dateArrival', value: string): void;
    (e: 'update:dateDeparture', value: string): void;
    (e: 'update:timeOneWay', value: string): void;
    (e: 'update:timeArrival', value: string): void;
    (e: 'update:timeDeparture', value: string): void;
    (e: 'previous'): void;
    (e: 'next'): void;
}>();

const totalPassengers = computed(() => props.adults + props.children + props.babies);
const maxPassengers = 16;
const isOverCapacity = computed(() => totalPassengers.value > 16);
const remainingPassengers = computed(() => Math.max(0, maxPassengers - totalPassengers.value));
const passengerFillPercentage = computed(() => Math.min(100, Math.round((totalPassengers.value / maxPassengers) * 100)));

const isFromValid = computed(() => !!props.from);
const isDropToValid = computed(() => !!props.dropTo && props.from !== props.dropTo);
const isDateValid = computed(() => {
    if (props.tripType === 'oneway') {
        return !!props.dateOneWay;
    }
    return !!props.dateArrival && !!props.dateDeparture;
});
const isTimeValid = computed(() => {
    if (props.tripType === 'oneway') {
        return !!props.timeOneWay;
    }
    return !!props.timeArrival && !!props.timeDeparture;
});

const passengerRangeZeroTo16 = Array.from({ length: 17 }, (_, i) => i);
const passengerRangeOneTo16 = passengerRangeZeroTo16.slice(1);

interface InlineSelectOption {
    value: string;
    label: string;
    disabled?: boolean;
}

const hotelGroups = computed(() => {
    // Get translations object directly from page props
    const page = usePage();
    const translations = (page.props as any).translations || {};
    const locale = (page.props as any).locale || 'en';
    const currentTranslations = translations[locale] || translations.en || {};
    const hotels = currentTranslations.reservation?.step1?.hotels || {};

    // Filter out non-array properties (like "title")
    const groups: Record<string, string[]> = {};
    for (const key in hotels) {
        if (Array.isArray(hotels[key])) {
            groups[key] = hotels[key];
        }
    }
    return groups;
});

const fromOptions = computed<InlineSelectOption[]>(() => [
    { value: '', label: t('reservation.step1.selectDeparture') },
    ...props.locationsWithKeys.map(([key, label]) => ({
        value: key,
        label: String(label),
        disabled: props.dropTo === key,
    })),
]);

const dropToOptions = computed<InlineSelectOption[]>(() => [
    { value: '', label: t('reservation.step1.selectDestination') },
    ...props.locationsWithKeys.map(([key, label]) => ({
        value: key,
        label: String(label),
        disabled: props.from === key,
    })),
]);

const hotelGroupedOptions = computed<Record<string, InlineSelectOption[]>>(() => {
    const grouped: Record<string, InlineSelectOption[]> = {};
    for (const [group, hotels] of Object.entries(hotelGroups.value)) {
        grouped[group] = hotels.map((hotelName) => ({
            value: hotelName,
            label: hotelName,
        }));
    }
    return grouped;
});

const passengerOptionsOneTo16 = computed<InlineSelectOption[]>(() => passengerRangeOneTo16.map((n) => ({ value: String(n), label: String(n) })));

const passengerOptionsZeroTo16 = computed<InlineSelectOption[]>(() => passengerRangeZeroTo16.map((n) => ({ value: String(n), label: String(n) })));

function getDisplayValue(options: InlineSelectOption[], selectedValue: string, placeholder: string) {
    if (!selectedValue) return placeholder;
    const selectedOption = options.find((option) => option.value === selectedValue);
    return selectedOption?.label ?? selectedValue;
}

const fromDisplayValue = computed(() => getDisplayValue(fromOptions.value, props.from, t('reservation.step1.selectDeparture')));
const dropToDisplayValue = computed(() => getDisplayValue(dropToOptions.value, props.dropTo, t('reservation.step1.selectDestination')));
const hotelDisplayValue = computed(() => props.hotel || t('reservation.step1.selectDestination'));
const adultsDisplayValue = computed(() => String(props.adults));
const childrenDisplayValue = computed(() => String(props.children));
const babiesDisplayValue = computed(() => String(props.babies));

function timeToMinutes(time: string): number {
    if (!time) return 0;
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
}

function addDaysToDate(dateStr: string, days: number): string {
    if (!dateStr) return dateStr;
    const date = new Date(dateStr);
    date.setDate(date.getDate() + days);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function buildPickupDisplay(date: string, time: string) {
    if (date && time) return `${date} - ${time}`;
    if (date) return date;
    if (time) return time;
    return '-';
}

function calculatePickupDateAndTime(tripDate: string, tripTime: string, pickupTime: string): { date: string; time: string } {
    if (!tripDate || !tripTime || !pickupTime) {
        return { date: tripDate || '', time: pickupTime || '' };
    }

    // Comparé heure de pickup avec heure de départ
    const tripTimeMinutes = timeToMinutes(tripTime);
    const pickupTimeMinutes = timeToMinutes(pickupTime);

    // Si pickupTime > tripTime en minutes, c'est que le pickup est la veille
    // (ex: trip 02:00, pickup 23:00 = la veille)
    const isPickupPreviousDay = pickupTimeMinutes > tripTimeMinutes;
    const pickupDate = isPickupPreviousDay ? addDaysToDate(tripDate, -1) : tripDate;

    return { date: pickupDate, time: pickupTime };
}

const oneWayPickupDateAndTime = computed(() => calculatePickupDateAndTime(props.dateOneWay, props.timeOneWay, props.pickUpTimeOneWay));
const departurePickupDateAndTime = computed(() => calculatePickupDateAndTime(props.dateDeparture, props.timeDeparture, props.pickUpTimeDeparture));

const oneWayPickupDisplay = computed(() => {
    const { date, time } = oneWayPickupDateAndTime.value;
    return buildPickupDisplay(date, time);
});
const departurePickupDisplay = computed(() => {
    const { date, time } = departurePickupDateAndTime.value;
    return buildPickupDisplay(date, time);
});

const canProceed = computed(() => {
    const locationsOk = props.from && props.dropTo && props.from !== props.dropTo;
    const paxOk = totalPassengers.value > 0 && !isOverCapacity.value;
    const timeOk = props.tripType === 'oneway' ? !!props.dateOneWay : !!props.dateArrival && !!props.dateDeparture;
    return locationsOk && paxOk && timeOk;
});

function handleNext() {
    attemptedNext.value = true;
    if (canProceed.value) {
        emit('next');
    }
}

const fieldContainerBaseClass =
    'group flex w-full min-w-0 items-center gap-3 rounded-full border border-transparent bg-dark px-4 py-2 text-primary-lighter shadow-lg transition-all duration-200 hover:border-primary-medium hover:bg-primary-dark focus-within:ring-2 focus-within:ring-blue-300';

function fieldContainerClass(isValid: boolean) {
    if (!attemptedNext.value || isValid) {
        return fieldContainerBaseClass;
    }

    return `${fieldContainerBaseClass} border-red-500 hover:border-red-500 focus-within:ring-red-400`;
}

function fieldInputClass(isValid: boolean) {
    if (!attemptedNext.value || isValid) {
        return 'w-full min-w-0 rounded-xl bg-primary-light p-1 px-3 text-lg font-bold tracking-wide text-dark focus:outline-none';
    }

    return 'w-full min-w-0 rounded-xl bg-red-50 p-1 px-3 text-lg font-bold tracking-wide text-red-700 placeholder:text-red-400 focus:outline-none';
}

const disabledFieldInputClass =
    'w-full min-w-0 rounded-xl bg-gray-200 p-1 px-3 text-lg font-bold tracking-wide text-gray-600 disabled:cursor-not-allowed focus:outline-none';

defineExpose({
    handleNext,
});
</script>

<template>
    <section class="space-y-6">
        <!-- One Way / Round Trip -->
        <ToggleSwitch
            :model-value="tripType"
            @update:model-value="emit('update:tripType', $event as 'oneway' | 'roundtrip')"
            :options="[
                { label: t('reservation.step1.oneWay'), value: 'oneway' },
                { label: t('reservation.step1.roundTrip'), value: 'roundtrip' },
            ]"
        />

        <!-- Arrival / Departure (only for One Way) -->
        <div v-if="tripType === 'oneway'">
            <ToggleSwitch
                :model-value="onewayDirection"
                @update:model-value="emit('update:onewayDirection', $event as 'arrival' | 'departure')"
                :options="[
                    { label: t('reservation.step1.arrival'), value: 'arrival' },
                    { label: t('reservation.step1.departure'), value: 'departure' },
                ]"
            />
        </div>

        <!-- Locations -->
        <div>
            <SelectButton
                :label="t('reservation.step1.from')"
                :value="fromDisplayValue"
                :model-value="from"
                :options="fromOptions"
                required
                @update:model-value="emit('update:from', $event)"
            />
            <p v-if="attemptedNext && !isFromValid" class="mt-1 text-sm text-red-600">{{ t('reservation.required.from') }}</p>
        </div>

        <!-- Hotel selection if "from" is Disneyland Paris -->
        <div v-if="from === 'disneyland'">
            <SelectButton
                :label="t('reservation.step1.hotels.title')"
                :value="hotelDisplayValue"
                :model-value="hotel"
                :options="[{ value: '', label: t('reservation.step1.selectDestination') }]"
                :grouped-options="hotelGroupedOptions"
                @update:model-value="emit('update:hotel', $event)"
            />
        </div>

        <div>
            <SelectButton
                :label="t('reservation.step1.to')"
                :value="dropToDisplayValue"
                :model-value="dropTo"
                :options="dropToOptions"
                required
                @update:model-value="emit('update:dropTo', $event)"
            />
            <p v-if="attemptedNext && !isDropToValid" class="mt-1 text-sm text-red-600">{{ t('reservation.required.to') }}</p>
        </div>

        <!-- Hotel selection if "dropTo" is Disneyland Paris -->
        <div v-if="dropTo === 'disneyland'">
            <SelectButton
                :label="t('reservation.step1.hotels.title')"
                :value="hotelDisplayValue"
                :model-value="hotel"
                :options="[{ value: '', label: t('reservation.step1.selectDestination') }]"
                :grouped-options="hotelGroupedOptions"
                @update:model-value="emit('update:hotel', $event)"
            />
        </div>

        <p v-if="from && dropTo && from === dropTo" class="text-sm text-red-600">{{ t('reservation.step1.sameLocation') }}</p>

        <hr class="border-t-2 border-primary-dark/30" />

        <!-- Passengers: adults / children / babies -->
        <div class="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-3">
            <SelectButton
                class="w-full"
                :label="t('reservation.step1.adults')"
                :value="adultsDisplayValue"
                :model-value="adultsDisplayValue"
                :options="passengerOptionsOneTo16"
                required
                isFull
                @update:model-value="emit('update:adults', Number($event))"
            />
            <SelectButton
                class="w-full"
                :label="t('reservation.step1.children')"
                :value="childrenDisplayValue"
                :model-value="childrenDisplayValue"
                :options="passengerOptionsZeroTo16"
                isFull
                @update:model-value="emit('update:children', Number($event))"
            />
            <SelectButton
                class="w-full"
                :label="t('reservation.step1.babies')"
                :value="babiesDisplayValue"
                :model-value="babiesDisplayValue"
                :options="passengerOptionsZeroTo16"
                isFull
                @update:model-value="emit('update:babies', Number($event))"
            />
        </div>
        <div
            :class="[
                'mt-1 space-y-4 rounded-2xl border px-4 py-4 shadow-lg transition-all duration-200',
                isOverCapacity ? 'border-red-300 bg-red-50' : 'border-primary-medium bg-dark text-primary-lighter',
            ]"
        >
            <div class="flex flex-wrap items-center justify-between gap-2">
                <p :class="['text-sm font-semibold', isOverCapacity ? 'text-red-800' : 'text-primary-lighter']">
                    {{ t('reservation.step1.total') }}: {{ totalPassengers }} / {{ maxPassengers }}
                </p>
                <span
                    :class="[
                        'rounded-full px-3 py-1 text-xs font-bold tracking-wide',
                        isOverCapacity ? 'bg-red-100 text-red-700' : 'bg-primary-lighter text-dark',
                    ]"
                >
                    {{
                        isOverCapacity ? t('reservation.step1.maxPassengers') : t('reservation.step1.remainingSeats', { count: remainingPassengers })
                    }}
                </span>
            </div>

            <div class="space-y-1">
                <div
                    class="h-3 overflow-hidden rounded-full border"
                    :class="isOverCapacity ? 'border-red-300 bg-red-100/70' : 'border-primary-medium bg-primary-light/20'"
                    role="progressbar"
                    :aria-valuemin="0"
                    :aria-valuemax="maxPassengers"
                    :aria-valuenow="Math.min(totalPassengers, maxPassengers)"
                    :aria-valuetext="`${totalPassengers} / ${maxPassengers}`"
                >
                    <div
                        :class="[
                            'h-full rounded-full transition-all duration-300',
                            isOverCapacity ? 'bg-red-500' : 'bg-gradient-to-r from-primary to-primary-dark',
                        ]"
                        :style="{ width: `${passengerFillPercentage}%` }"
                    ></div>
                </div>
                <div
                    class="flex items-center justify-between text-[11px] font-medium"
                    :class="isOverCapacity ? 'text-red-700' : 'text-primary-light'"
                >
                    <span>0</span>
                    <span>{{ maxPassengers }}</span>
                </div>
            </div>

            <div class="grid grid-cols-3 gap-2 text-xs font-semibold">
                <div
                    :class="[
                        'rounded-xl border px-3 py-2 text-center',
                        isOverCapacity ? 'border-red-200 bg-white text-red-800' : 'border-primary-medium bg-primary-lighter text-dark',
                    ]"
                >
                    {{ t('reservation.step1.adults') }}: {{ adults }}
                </div>
                <div
                    :class="[
                        'rounded-xl border px-3 py-2 text-center',
                        isOverCapacity ? 'border-red-200 bg-white text-red-800' : 'border-primary-medium bg-primary-lighter text-dark',
                    ]"
                >
                    {{ t('reservation.step1.children') }}: {{ children }}
                </div>
                <div
                    :class="[
                        'rounded-xl border px-3 py-2 text-center',
                        isOverCapacity ? 'border-red-200 bg-white text-red-800' : 'border-primary-medium bg-primary-lighter text-dark',
                    ]"
                >
                    {{ t('reservation.step1.babies') }}: {{ babies }}
                </div>
            </div>
        </div>

        <hr class="border-t-2 border-primary-dark/30" />

        <!-- Dates & Times -->
        <div v-if="tripType === 'oneway'" class="space-y-4">
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                    <FormDateInput
                        :model-value="dateOneWay"
                        :label="t('reservation.step1.date')"
                        :min="minDate"
                        :hint="dateFormatHint"
                        :error="t('reservation.required.date')"
                        :show-error="attemptedNext && !isDateValid"
                        required
                        @update:model-value="emit('update:dateOneWay', $event)"
                    />
                </div>
                <div>
                    <div :class="fieldContainerClass(isTimeValid)">
                        <span class="shrink-0 text-lg font-medium whitespace-nowrap text-primary-light italic">
                            {{ t('reservation.step1.time') }} <span class="text-red-600">*</span> :
                        </span>
                        <input
                            :value="timeOneWay"
                            @input="emit('update:timeOneWay', ($event.target as HTMLInputElement).value)"
                            type="time"
                            :class="[fieldInputClass(isTimeValid), 'flex-1']"
                        />
                    </div>
                    <p v-if="attemptedNext && !isTimeValid" class="mt-1 text-sm text-red-600">{{ t('reservation.required.time') }}</p>
                </div>
            </div>
            <div v-if="onewayDirection === 'departure'">
                <div :class="fieldContainerClass(true)">
                    <span class="shrink-0 text-lg font-medium whitespace-nowrap text-primary-light italic">
                        {{ t('reservation.step1.pickUpTime') }} :
                    </span>
                    <input :value="oneWayPickupDisplay" type="text" disabled :class="[disabledFieldInputClass, 'flex-1']" />
                </div>
            </div>
        </div>

        <div v-else class="space-y-6">
            <!-- Arrival -->
            <div class="space-y-3">
                <h3 class="font-semibold">{{ t('reservation.step1.arrival') }}</h3>
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <FormDateInput
                            :model-value="dateArrival"
                            :label="t('reservation.step1.date')"
                            :min="minDate"
                            :hint="dateFormatHint"
                            :error="t('reservation.required.date')"
                            :show-error="attemptedNext && tripType === 'roundtrip' && !dateArrival"
                            required
                            @update:model-value="emit('update:dateArrival', $event)"
                        />
                    </div>
                    <div>
                        <div :class="fieldContainerClass(!!timeArrival)">
                            <span class="shrink-0 text-lg font-medium whitespace-nowrap text-primary-light italic">
                                {{ t('reservation.step1.time') }} <span class="text-red-600">*</span> :
                            </span>
                            <input
                                :value="timeArrival"
                                @input="emit('update:timeArrival', ($event.target as HTMLInputElement).value)"
                                type="time"
                                :class="[fieldInputClass(!!timeArrival), 'flex-1']"
                            />
                        </div>
                        <p v-if="attemptedNext && tripType === 'roundtrip' && !timeArrival" class="mt-1 text-sm text-red-600">
                            {{ t('reservation.required.time') }}
                        </p>
                    </div>
                </div>
            </div>
            <!-- Departure -->
            <div class="space-y-3">
                <h3 class="font-semibold">{{ t('reservation.step1.departure') }}</h3>
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <FormDateInput
                            :model-value="dateDeparture"
                            :label="t('reservation.step1.date')"
                            :min="minDate"
                            :hint="dateFormatHint"
                            :error="t('reservation.required.date')"
                            :show-error="attemptedNext && tripType === 'roundtrip' && !dateDeparture"
                            required
                            @update:model-value="emit('update:dateDeparture', $event)"
                        />
                    </div>
                    <div>
                        <div :class="fieldContainerClass(!!timeDeparture)">
                            <span class="shrink-0 text-lg font-medium whitespace-nowrap text-primary-light italic">
                                {{ t('reservation.step1.time') }} <span class="text-red-600">*</span> :
                            </span>
                            <input
                                :value="timeDeparture"
                                @input="emit('update:timeDeparture', ($event.target as HTMLInputElement).value)"
                                type="time"
                                :class="[fieldInputClass(!!timeDeparture), 'flex-1']"
                            />
                        </div>
                        <p v-if="attemptedNext && tripType === 'roundtrip' && !timeDeparture" class="mt-1 text-sm text-red-600">
                            {{ t('reservation.required.time') }}
                        </p>
                    </div>
                </div>
                <div>
                    <div :class="fieldContainerClass(true)">
                        <span class="shrink-0 text-lg font-medium whitespace-nowrap text-primary-light italic">
                            {{ t('reservation.step1.pickUpTime') }} :
                        </span>
                        <input :value="departurePickupDisplay" type="text" disabled :class="[disabledFieldInputClass, 'flex-1']" />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
